import { useCallback, useEffect, useState } from "react";
import { HardHat, Loader2, Plus, ShieldCheck, Wrench } from "lucide-react";
import { api, apiError, logError, shortDate, uploadFile } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const INSTALL_STATUSES = ["Pending", "Scheduled", "In Progress", "Installed"];
const TICKET_STATUSES = ["Open", "In Progress", "Resolved"];
const PRIORITIES = ["Low", "Medium", "High"];

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="kpi border-b border-r border-border">
      <div className="overline">{label}</div>
      <div className="mt-2 font-mono-num text-2xl font-semibold">{value}</div>
    </div>
  );
}

function InstallationDialog({ challan, onSaved, onClose }) {
  const [form, setForm] = useState({
    site_address: "", scheduled_date: "", installed_date: "", engineer_name: "",
    team: "", signoff_note: "", status: "Pending", photo_ids: [],
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!challan) return;
    api.get(`/challans/${challan.id}/installation`)
      .then(({ data }) => data && setForm((f) => ({ ...f, ...data })))
      .catch((e) => logError("installation", e));
  }, [challan]);

  const save = async () => {
    setBusy(true);
    try {
      const { site_address, scheduled_date, installed_date, engineer_name, team,
        signoff_note, status, photo_ids } = form;
      await api.put(`/challans/${challan.id}/installation`, {
        site_address, scheduled_date, installed_date, engineer_name, team,
        signoff_note, status, photo_ids: photo_ids || [],
      });
      toast.success("Installation record saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="installation-dialog">
        <DialogHeader>
          <DialogTitle>Installation — {challan.number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Textarea placeholder="Site address" data-testid="install-site-input"
            value={form.site_address}
            onChange={(e) => setForm({ ...form, site_address: e.target.value })} />
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs text-muted-foreground">
              Scheduled
              <Input type="date" data-testid="install-scheduled-input" value={form.scheduled_date || ""}
                onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} />
            </label>
            <label className="text-xs text-muted-foreground">
              Installed on
              <Input type="date" data-testid="install-date-input" value={form.installed_date || ""}
                onChange={(e) => setForm({ ...form, installed_date: e.target.value })} />
            </label>
          </div>
          <Input placeholder="Engineer name" data-testid="install-engineer-input"
            value={form.engineer_name}
            onChange={(e) => setForm({ ...form, engineer_name: e.target.value })} />
          <Input placeholder="Team / helpers" data-testid="install-team-input" value={form.team}
            onChange={(e) => setForm({ ...form, team: e.target.value })} />
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="install-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INSTALL_STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`install-status-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea placeholder="Customer sign-off note" data-testid="install-signoff-input"
            value={form.signoff_note}
            onChange={(e) => setForm({ ...form, signoff_note: e.target.value })} />
          <label className="block text-xs text-muted-foreground">
            Site photos
            <Input type="file" accept="image/*" multiple data-testid="install-photo-input"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                try {
                  const ids = [];
                  for (const f of files) {
                    const up = await uploadFile(f, "installation", challan.id);
                    ids.push(up.id);
                  }
                  setForm((prev) => ({ ...prev, photo_ids: [...(prev.photo_ids || []), ...ids] }));
                  toast.success(`${ids.length} photo(s) attached`);
                } catch (err) {
                  toast.error(apiError(err));
                }
              }} />
          </label>
          {(form.photo_ids || []).length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="install-photo-count">
              {form.photo_ids.length} photo(s) attached
            </p>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="install-save-button" onClick={save} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WarrantyDialog({ challan, onSaved, onClose }) {
  const [form, setForm] = useState({ item_name: "", start_date: "", months: 12, terms: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/challans/${challan.id}/warranties`, { ...form, months: Number(form.months) });
      toast.success("Warranty recorded");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="warranty-dialog">
        <DialogHeader>
          <DialogTitle>Warranty — {challan.number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Select value={form.item_name} onValueChange={(v) => setForm({ ...form, item_name: v })}>
            <SelectTrigger data-testid="warranty-item-select">
              <SelectValue placeholder="Choose the dispatched item" />
            </SelectTrigger>
            <SelectContent>
              {(challan.items || []).map((it) => (
                <SelectItem key={it.name} value={it.name} data-testid={`warranty-item-${it.name}`}>{it.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs text-muted-foreground">
              Start date
              <Input type="date" data-testid="warranty-start-input" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </label>
            <label className="text-xs text-muted-foreground">
              Months
              <Input type="number" data-testid="warranty-months-input" value={form.months}
                onChange={(e) => setForm({ ...form, months: e.target.value })} />
            </label>
          </div>
          <Textarea placeholder="Warranty terms" data-testid="warranty-terms-input" value={form.terms}
            onChange={(e) => setForm({ ...form, terms: e.target.value })} />
        </div>
        <DialogFooter>
          <Button data-testid="warranty-save-button" onClick={save}
            disabled={busy || !form.item_name}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save warranty
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketDialog({ warranties, onSaved, onClose }) {
  const [form, setForm] = useState({ warranty_id: "", issue: "", priority: "Medium" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/service-tickets", form);
      toast.success("Complaint logged");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle>Log a service complaint</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Select value={form.warranty_id} onValueChange={(v) => setForm({ ...form, warranty_id: v })}>
            <SelectTrigger data-testid="ticket-warranty-select">
              <SelectValue placeholder="Against which warranty (optional)" />
            </SelectTrigger>
            <SelectContent>
              {warranties.map((w) => (
                <SelectItem key={w.id} value={w.id} data-testid={`ticket-warranty-${w.id}`}>
                  {w.item_name} — {w.customer_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea placeholder="What is the problem?" data-testid="ticket-issue-input"
            value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} />
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button data-testid="ticket-save-button" onClick={save} disabled={busy || !form.issue.trim()}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log complaint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ServiceWarranty() {
  const [summary, setSummary] = useState({});
  const [challans, setChallans] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [installFor, setInstallFor] = useState(null);
  const [warrantyFor, setWarrantyFor] = useState(null);
  const [newTicket, setNewTicket] = useState(false);
  const [resolution, setResolution] = useState({});

  const load = useCallback(() => {
    api.get("/service/summary").then(({ data }) => setSummary(data)).catch((e) => logError("summary", e));
    api.get("/challans?status=Dispatched").then(({ data }) => setChallans(data))
      .catch((e) => logError("challans", e));
    api.get("/installations").then(({ data }) => setInstallations(data))
      .catch((e) => logError("installations", e));
    api.get("/warranties").then(({ data }) => setWarranties(data)).catch((e) => logError("warranties", e));
    api.get("/service-tickets").then(({ data }) => setTickets(data)).catch((e) => logError("tickets", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusOf = (cid) => installations.find((i) => i.challan_id === cid)?.status || "Pending";

  const updateTicket = async (t, status) => {
    try {
      await api.put(`/service-tickets/${t.id}`, { status, resolution: resolution[t.id] || t.resolution || "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        testId="service-header"
        title="Installation, warranty & service"
        subtitle="What happens after the truck leaves — site installation, warranty cover and complaints."
        actions={
          <Button className="gap-2" data-testid="new-ticket-button" onClick={() => setNewTicket(true)}>
            <Plus className="h-4 w-4" /> Log complaint
          </Button>
        }
      />

      <div className="grid grid-cols-2 border-b border-border lg:grid-cols-5">
        <Stat label="Installations pending" value={summary.installations_pending ?? 0} testId="stat-install-pending" />
        <Stat label="Installed" value={summary.installed ?? 0} testId="stat-installed" />
        <Stat label="Warranties active" value={summary.warranties_active ?? 0} testId="stat-warranty-active" />
        <Stat label="Expiring in 30 days" value={summary.warranties_expiring_30d ?? 0} testId="stat-warranty-expiring" />
        <Stat label="Open complaints" value={summary.tickets_open ?? 0} testId="stat-tickets-open" />
      </div>

      <Tabs defaultValue="installations" className="p-4">
        <TabsList>
          <TabsTrigger value="installations" data-testid="tab-installations">
            <HardHat className="mr-1.5 h-3.5 w-3.5" /> Installations
          </TabsTrigger>
          <TabsTrigger value="warranties" data-testid="tab-warranties">
            <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Warranties
          </TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">
            <Wrench className="mr-1.5 h-3.5 w-3.5" /> Complaints
          </TabsTrigger>
        </TabsList>

        <TabsContent value="installations" data-testid="installations-list">
          {challans.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No dispatched challans yet — installation is recorded after dispatch.
            </p>
          ) : (
            challans.map((c) => (
              <div key={c.id} data-testid={`install-row-${c.id}`}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-3 last:border-0">
                <div className="min-w-0">
                  <div className="font-medium">{c.number}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.customer_name} • dispatched {shortDate(c.dispatched_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" data-testid={`install-status-badge-${c.id}`}>{statusOf(c.id)}</Badge>
                  <Button size="sm" variant="outline" data-testid={`install-open-${c.id}`}
                    onClick={() => setInstallFor(c)}>Installation</Button>
                  <Button size="sm" variant="outline" data-testid={`warranty-open-${c.id}`}
                    onClick={() => setWarrantyFor(c)}>Add warranty</Button>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="warranties" data-testid="warranties-list">
          {warranties.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No warranties recorded yet.</p>
          ) : (
            warranties.map((w) => (
              <div key={w.id} data-testid={`warranty-row-${w.id}`}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-3 last:border-0">
                <div className="min-w-0">
                  <div className="font-medium">{w.item_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {w.customer_name} • {w.challan_number} • {w.months} months from {shortDate(w.start_date)}
                  </div>
                </div>
                <Badge variant={w.expired ? "destructive" : "outline"} data-testid={`warranty-expiry-${w.id}`}>
                  {w.expired ? "expired" : "till"} {shortDate(w.expiry_date)}
                </Badge>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="tickets" data-testid="tickets-list">
          {tickets.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No complaints logged.</p>
          ) : (
            tickets.map((t) => (
              <div key={t.id} className="border-b border-border py-3 last:border-0"
                data-testid={`ticket-row-${t.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">{t.item_name || "Service request"}</div>
                    <div className="text-xs text-muted-foreground">
                      {t.customer_name} • {t.issue}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{t.priority}</Badge>
                    <Badge variant={t.in_warranty ? "outline" : "destructive"}>
                      {t.in_warranty ? "in warranty" : "out of warranty"}
                    </Badge>
                    <Badge data-testid={`ticket-status-${t.id}`}>{t.status}</Badge>
                  </div>
                </div>
                {t.status !== "Resolved" && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Input className="h-8 max-w-sm" placeholder="How was it resolved?"
                      data-testid={`ticket-resolution-${t.id}`}
                      value={resolution[t.id] || ""}
                      onChange={(e) => setResolution({ ...resolution, [t.id]: e.target.value })} />
                    <Button size="sm" variant="outline" data-testid={`ticket-progress-${t.id}`}
                      onClick={() => updateTicket(t, "In Progress")}>In progress</Button>
                    <Button size="sm" data-testid={`ticket-resolve-${t.id}`}
                      onClick={() => updateTicket(t, "Resolved")}>Resolve</Button>
                  </div>
                )}
                {t.resolution && (
                  <p className="mt-1 text-xs text-muted-foreground">Resolution: {t.resolution}</p>
                )}
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>

      {installFor && (
        <InstallationDialog challan={installFor} onSaved={load} onClose={() => setInstallFor(null)} />
      )}
      {warrantyFor && (
        <WarrantyDialog challan={warrantyFor} onSaved={load} onClose={() => setWarrantyFor(null)} />
      )}
      {newTicket && (
        <TicketDialog warranties={warranties} onSaved={load} onClose={() => setNewTicket(false)} />
      )}
    </div>
  );
}
